import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { apiRequest } from '../utils/api';

interface InstagramCookieAccount {
  id: number;
  label: string | null;
  username: string | null;
  cookiePreview: string;
  proxyId: number | null;
  proxy: { id: number; ipVersion: string; proxy: string; isActive: boolean } | null;
  isActive: boolean;
  status: string;
  usageCount: number;
  lastUsedAt: string | null;
  failCount: number;
  lastFailedAt: string | null;
  disabledUntil: string | null;
  lastError: string | null;
  createdAt: string;
}

interface ProxyOption {
  id: number;
  ipVersion: string;
  proxy: string;
  usageCount: number;
  lastUsedAt: string | null;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  padding: '9px 10px',
  fontSize: '14px',
  minHeight: '38px',
  boxSizing: 'border-box'
};

const buttonBase: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  padding: '9px 12px',
  cursor: 'pointer',
  fontWeight: 600,
  background: '#fff',
  color: '#111827'
};

const primaryButton: React.CSSProperties = {
  ...buttonBase,
  background: '#2563eb',
  borderColor: '#2563eb',
  color: '#fff'
};

const dangerButton: React.CSSProperties = {
  ...buttonBase,
  background: '#fee2e2',
  borderColor: '#fecaca',
  color: '#991b1b'
};

const thStyle: React.CSSProperties = {
  padding: '11px 12px',
  textAlign: 'left',
  color: '#6b7280',
  fontSize: '12px',
  fontWeight: 700,
  textTransform: 'uppercase',
  borderBottom: '1px solid #e5e7eb'
};

const tdStyle: React.CSSProperties = {
  padding: '12px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'top',
  fontSize: '13px'
};

function formatDate(value: string | null) {
  return value ? new Date(value).toLocaleString('vi-VN') : 'Chưa dùng';
}

function getErrorMessage(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function statusLabel(status: string, isActive: boolean) {
  if (!isActive) return 'Đang tắt';
  if (status === 'limited') return 'Tạm khóa';
  if (status === 'disabled') return 'Vô hiệu';
  if (status === 'error') return 'Lỗi';
  return 'Hoạt động';
}

function statusStyle(status: string, isActive: boolean): React.CSSProperties {
  if (!isActive || status === 'disabled') return { background: '#f3f4f6', color: '#374151' };
  if (status === 'limited' || status === 'error') return { background: '#fef3c7', color: '#92400e' };
  return { background: '#d1fae5', color: '#065f46' };
}

const InstagramCookies: React.FC = () => {
  const { hasAnyPermission, hasPermission } = useAuth();
  const [accounts, setAccounts] = useState<InstagramCookieAccount[]>([]);
  const [proxies, setProxies] = useState<ProxyOption[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, withProxy: 0, withoutProxy: 0 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, hasNext: false });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [proxyFilter, setProxyFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedCookies, setSelectedCookies] = useState<Set<number>>(new Set());
  const [selectedProxies, setSelectedProxies] = useState<Set<number>>(new Set());
  const [importContent, setImportContent] = useState('');
  const [accountsPerProxy, setAccountsPerProxy] = useState(1);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const selectedCookieCount = selectedCookies.size;
  const selectedProxyCount = selectedProxies.size;
  const allVisibleSelected = accounts.length > 0 && accounts.every(item => selectedCookies.has(item.id));


  const loadData = async (nextPage = page) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: String(nextPage),
        limit: '50',
        status: statusFilter,
        proxy: proxyFilter
      });
      if (search.trim()) params.set('search', search.trim());

      const [accountResponse, proxyResponse] = await Promise.all([
        apiRequest('/api/social-count-management/instagram-cookies?' + params.toString()),
        apiRequest('/api/social-count-management/instagram-cookies/proxy-options')
      ]);

      const accountData = await accountResponse.json();
      if (!accountResponse.ok) throw new Error(accountData.error || 'Không thể tải cookie');
      setAccounts(accountData.data || []);
      setStats(accountData.stats || { total: 0, active: 0, inactive: 0, withProxy: 0, withoutProxy: 0 });
      setPagination(accountData.pagination || { page: nextPage, pages: 1, total: 0, hasNext: false });
      setPage(nextPage);

      if (proxyResponse.ok) {
        const proxyData = await proxyResponse.json();
        setProxies(proxyData.data || []);
      }
    } catch (error) {
      alert(getErrorMessage(error, 'Không thể tải dữ liệu'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCookie = (id: number) => {
    setSelectedCookies(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleVisibleCookies = () => {
    setSelectedCookies(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        accounts.forEach(item => next.delete(item.id));
      } else {
        accounts.forEach(item => next.add(item.id));
      }
      return next;
    });
  };

  const toggleProxy = (id: number) => {
    setSelectedProxies(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const importCookies = async () => {
    if (!importContent.trim()) {
      alert('Dán cookie trước khi import');
      return;
    }
    try {
      setImporting(true);
      const response = await apiRequest('/api/social-count-management/instagram-cookies/import', {
        method: 'POST',
        body: JSON.stringify({ content: importContent, isActive: true })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Không thể import cookie');
      alert('Đã import ' + (data.created || 0) + ' cookie, bỏ qua ' + (data.skipped || 0) + ', lỗi ' + (data.failed || 0));
      setImportContent('');
      await loadData(1);
    } catch (error) {
      alert(getErrorMessage(error, 'Không thể import cookie'));
    } finally {
      setImporting(false);
    }
  };

  const assignProxies = async () => {
    if (selectedCookieCount === 0) {
      alert('Chọn ít nhất một cookie');
      return;
    }
    if (selectedProxyCount === 0) {
      alert('Chọn ít nhất một proxy');
      return;
    }
    try {
      setAssigning(true);
      const response = await apiRequest('/api/social-count-management/instagram-cookies/assign-proxies', {
        method: 'POST',
        body: JSON.stringify({
          cookieIds: Array.from(selectedCookies),
          proxyIds: Array.from(selectedProxies),
          accountsPerProxy: Number(accountsPerProxy) || 1
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Không thể gán proxy');
      alert('Đã gán proxy cho ' + (data.assigned || 0) + ' cookie');
      await loadData(page);
    } catch (error) {
      alert(getErrorMessage(error, 'Không thể gán proxy'));
    } finally {
      setAssigning(false);
    }
  };

  const updateAccount = async (account: InstagramCookieAccount, patch: Record<string, unknown>) => {
    try {
      const response = await apiRequest('/api/social-count-management/instagram-cookies/' + account.id, {
        method: 'PUT',
        body: JSON.stringify(patch)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Không thể lưu cookie');
      await loadData(page);
    } catch (error) {
      alert(getErrorMessage(error, 'Không thể lưu cookie'));
    }
  };

  const deleteAccount = async (account: InstagramCookieAccount) => {
    if (!window.confirm('Xóa cookie ' + (account.label || account.username || account.id) + '?')) return;
    try {
      const response = await apiRequest('/api/social-count-management/instagram-cookies/' + account.id, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Không thể xóa cookie');
      setSelectedCookies(prev => {
        const next = new Set(prev);
        next.delete(account.id);
        return next;
      });
      await loadData(page);
    } catch (error) {
      alert(getErrorMessage(error, 'Không thể xóa cookie'));
    }
  };

  if (!hasAnyPermission('social_count')) {
    return (
      <Layout currentPage="instagram-cookies">
        <div style={{ padding: 0 }}>
          <h1>Quản lý Cookie Instagram</h1>
          <p>Bạn không có quyền truy cập trang này.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="instagram-cookies">
      <div style={{ color: '#111827', width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '28px' }}>Quản lý Cookie Instagram</h1>
            <p style={{ margin: '6px 0 0', color: '#6b7280' }}>Lưu cookie account nội bộ và gán proxy để provider Instagram chính thức sử dụng.</p>
          </div>
          <button onClick={() => loadData(page)} disabled={loading} style={buttonBase}>Tải lại</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '18px' }}>
          {[
            ['Tổng cookie', stats.total, '#111827'],
            ['Đang hoạt động', stats.active, '#047857'],
            ['Đang tắt', stats.inactive, '#b91c1c'],
            ['Đã gán proxy', stats.withProxy, '#1d4ed8'],
            ['Chưa gán proxy', stats.withoutProxy, '#7c3aed']
          ].map(([label, value, color]) => (
            <div key={String(label)} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '14px' }}>
              <div style={{ color: '#6b7280', fontSize: '13px' }}>{label}</div>
              <div style={{ color: String(color), fontSize: '28px', fontWeight: 800 }}>{String(value)}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '16px', marginBottom: '18px' }}>
          <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', minWidth: 0, boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 10px', fontSize: '20px' }}>Import cookie</h2>
            <div style={{ color: '#6b7280', fontSize: '13px', marginBottom: '10px' }}>Mỗi dòng một cookie hoặc chỉ giá trị sessionid. Hệ thống tự lọc trùng theo sessionid.</div>
            <textarea
              value={importContent}
              onChange={event => setImportContent(event.target.value)}
              placeholder={'56000338709:QjaqYSXLm4wU4w:17:AYgDEB123zSji0WmHmulhYa9dzPVy32lSV5_SkU\nsessionid=...; ds_user=...; csrftoken=...'}
              style={{ ...inputStyle, minHeight: '150px', fontFamily: 'monospace', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
              <button onClick={importCookies} disabled={importing || !hasPermission('social_count', 'create')} style={primaryButton}>{importing ? 'Đang import...' : 'Import cookie'}</button>
            </div>
          </section>

          <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', minWidth: 0, boxSizing: 'border-box' }}>
            <h2 style={{ margin: '0 0 10px', fontSize: '20px' }}>Gán proxy cho cookie đã chọn</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', alignItems: 'end', marginBottom: '12px' }}>
              <label style={{ fontWeight: 600, fontSize: '14px' }}>
                Số account / proxy
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={accountsPerProxy}
                  onChange={event => setAccountsPerProxy(Number(event.target.value) || 1)}
                  style={{ ...inputStyle, marginTop: '6px' }}
                />
              </label>
              <div style={{ color: '#6b7280', fontSize: '13px' }}>
                Đã chọn {selectedCookieCount} cookie và {selectedProxyCount} proxy. Ví dụ nhập 3 nghĩa là 1 proxy sẽ gán cho 3 account trước khi chuyển sang proxy tiếp theo.
              </div>
              <button onClick={assignProxies} disabled={assigning || !hasPermission('social_count', 'update')} style={primaryButton}>{assigning ? 'Đang gán...' : 'Gán proxy'}</button>
            </div>
            <div style={{ maxHeight: '190px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
              {proxies.length === 0 ? (
                <div style={{ padding: '14px', color: '#6b7280' }}>Chưa có proxy active trong pool DB.</div>
              ) : proxies.map(proxy => (
                <label key={proxy.id} style={{ display: 'grid', gridTemplateColumns: '28px minmax(0, 1fr) auto', gap: '8px', alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid #f3f4f6' }}>
                  <input type="checkbox" checked={selectedProxies.has(proxy.id)} onChange={() => toggleProxy(proxy.id)} />
                  <span style={{ fontFamily: 'monospace', fontSize: '13px', minWidth: 0, overflowWrap: 'anywhere' }}>{proxy.proxy}</span>
                  <span style={{ color: '#6b7280', fontSize: '12px', textTransform: 'uppercase' }}>{proxy.ipVersion}</span>
                </label>
              ))}
            </div>
          </section>
        </div>

        <section style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          <div style={{ padding: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', alignItems: 'end', borderBottom: '1px solid #e5e7eb' }}>
            <label style={{ fontWeight: 600, fontSize: '14px' }}>
              Trạng thái
              <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} style={{ ...inputStyle, marginTop: '6px' }}>
                <option value="all">Tất cả</option>
                <option value="active">Đang hoạt động</option>
                <option value="inactive">Đang tắt</option>
                <option value="limited">Tạm khóa</option>
                <option value="disabled">Vô hiệu</option>
              </select>
            </label>
            <label style={{ fontWeight: 600, fontSize: '14px' }}>
              Proxy
              <select value={proxyFilter} onChange={event => setProxyFilter(event.target.value)} style={{ ...inputStyle, marginTop: '6px' }}>
                <option value="all">Tất cả</option>
                <option value="assigned">Đã gán</option>
                <option value="unassigned">Chưa gán</option>
              </select>
            </label>
            <label style={{ fontWeight: 600, fontSize: '14px' }}>
              Tìm kiếm
              <input value={search} onChange={event => setSearch(event.target.value)} onKeyDown={event => event.key === 'Enter' && loadData(1)} placeholder="Tên, username" style={{ ...inputStyle, marginTop: '6px' }} />
            </label>
            <button onClick={() => loadData(1)} disabled={loading} style={primaryButton}>Lọc</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb' }}>
                <tr>
                  <th style={thStyle}><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleCookies} /></th>
                  <th style={thStyle}>Cookie</th>
                  <th style={thStyle}>Proxy gán</th>
                  <th style={thStyle}>Trạng thái</th>
                  <th style={thStyle}>Đã dùng</th>
                  <th style={thStyle}>Fail</th>
                  <th style={thStyle}>Lỗi gần nhất</th>
                  <th style={thStyle}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#6b7280', padding: '32px' }}>Chưa có cookie Instagram.</td></tr>
                ) : accounts.map(account => (
                  <tr key={account.id}>
                    <td style={tdStyle}><input type="checkbox" checked={selectedCookies.has(account.id)} onChange={() => toggleCookie(account.id)} /></td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 700 }}>{account.label || account.username || 'Cookie #' + account.id}</div>
                      <div style={{ color: '#6b7280' }}>{account.username || 'Chưa nhận username'}</div>
                      <code style={{ display: 'block', marginTop: '5px', color: '#475569', fontSize: '12px' }}>{account.cookiePreview}</code>
                    </td>
                    <td style={tdStyle}>
                      {account.proxy ? (
                        <div>
                          <code style={{ overflowWrap: 'anywhere' }}>{account.proxy.proxy}</code>
                          <div style={{ color: '#6b7280', textTransform: 'uppercase', marginTop: '4px' }}>{account.proxy.ipVersion}</div>
                        </div>
                      ) : <span style={{ color: '#9ca3af' }}>Chưa gán</span>}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ ...statusStyle(account.status, account.isActive), borderRadius: '999px', padding: '4px 8px', fontWeight: 700, fontSize: '12px' }}>{statusLabel(account.status, account.isActive)}</span>
                      {account.disabledUntil && <div style={{ color: '#92400e', marginTop: '5px' }}>Mở lại: {formatDate(account.disabledUntil)}</div>}
                    </td>
                    <td style={tdStyle}>
                      <div>{account.usageCount.toLocaleString()}</div>
                      <div style={{ color: '#6b7280' }}>{formatDate(account.lastUsedAt)}</div>
                    </td>
                    <td style={tdStyle}>
                      <div>{account.failCount.toLocaleString()}</div>
                      <div style={{ color: '#6b7280' }}>{formatDate(account.lastFailedAt)}</div>
                    </td>
                    <td style={{ ...tdStyle, maxWidth: '260px', color: '#6b7280' }}>{account.lastError || '-'}</td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button style={buttonBase} onClick={() => updateAccount(account, { isActive: !account.isActive })}>{account.isActive ? 'Tắt' : 'Bật'}</button>
                        {account.proxyId && <button style={buttonBase} onClick={() => updateAccount(account, { clearProxy: true })}>Bỏ proxy</button>}
                        <button style={dangerButton} onClick={() => deleteAccount(account)} disabled={!hasPermission('social_count', 'delete')}>Xóa</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#6b7280', fontSize: '13px' }}>
            <div>Trang {pagination.page} / {Math.max(pagination.pages, 1)} - {pagination.total} cookie theo bộ lọc</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={page <= 1} onClick={() => loadData(page - 1)} style={{ ...buttonBase, opacity: page <= 1 ? 0.5 : 1 }}>Trang trước</button>
              <button disabled={!pagination.hasNext} onClick={() => loadData(page + 1)} style={{ ...buttonBase, opacity: !pagination.hasNext ? 0.5 : 1 }}>Trang sau</button>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default InstagramCookies;
